import { default as FNS, logIn } from '../api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

export const changeWaypointLocation = createAsyncThunk('inquery/changeLocation', async ({ id, latLng }, { dispatch }) => {
  dispatch(inquerySlice.actions.updateWaypoint({ id, location: latLng }));
});

export const updateRouteEstimation = createAsyncThunk('inquery/updateRouteEstimation', async (payload, { dispatch, getState }) => {
  const state = getState();

  // Prepare waypoints for the backend: remove 'depart' from the last one
  const waypointsForEstimation = state.inquery.waypoints.map((wp, index, array) => {
    // Create a copy to avoid modifying the original state directly here
    const waypointData = { ...wp };

    // If it's the last waypoint, delete the depart field
    if (index === array.length - 1) {
      delete waypointData.depart;
    }
    return waypointData;
  });

  // Send the modified waypoints array
  const res = await FNS.updateRouteEstimation(JSON.stringify(waypointsForEstimation));
  if(!res) return;

  // Assuming the backend returns the full route with updated 'arrive' fields
  // The existing reducer logic seems to handle the update correctly
  dispatch(inquerySlice.actions.updateRouteEstimation({ route: res }));
});

export const postQuery = createAsyncThunk('inquery/postQuery', async (payload, { dispatch, getState }) => {
  try {
    const state = getState();

    // Ensure location exists for all waypoints before sending
    const validWaypoints = state.inquery.waypoints.filter(p => p.location);
    if (validWaypoints.length < 1) { // Need at least one point
        throw new Error("Please define at least one location for the route.");
    }
    // Ensure first point has a depart time if there's more than one point
    if (validWaypoints.length > 1 && !validWaypoints[0].depart) {
        throw new Error("Please set a departure time for the starting point.");
    }

    // Modified: Conditionally add 'depart' key, omit for the last waypoint
    const routeForBackend = validWaypoints.map((p, index, array) => {
        const waypointData = {
            lat: p.location.lat,
            lng: p.location.lng,
            formatted_address: p.formatted_address,
        };
        // Add depart only if it's NOT the last waypoint
        if (index < array.length - 1) {
            waypointData.depart = p.depart;
        }
        return waypointData;
    });

    // Ensure the first waypoint has a depart time before sending
    if (!routeForBackend[0].depart && routeForBackend.length > 1) {
        // This case should ideally be caught earlier, but double-check
         throw new Error("Starting point departure time is missing.");
    }

    const res = await FNS.postQuery(
      JSON.stringify(routeForBackend),
      JSON.stringify(state.inquery.foodPackages),
      (+state.inquery.passengers),
      state.inquery.notes,
      state.inquery.fullname,
      state.inquery.promo,
      state.inquery.email,
      state.inquery.phone,
      state.inquery.company,
      state.inquery.brand,
    );

    dispatch(inquerySlice.actions.finish({ queryId: res }));

    return res;
  } catch (e) {
    toast.error(e.message);
    // Re-throw or handle the error appropriately so the promise rejects
    throw e;
  }
});

const DATE_TODAY = new Date();
const DATE_NEXT_WEEK = new Date(DATE_TODAY.getTime() + (1000*60*60*24*7));
const DATE_NEXT_WEEK_00 = new Date(DATE_NEXT_WEEK.getFullYear(), DATE_NEXT_WEEK.getMonth(), DATE_NEXT_WEEK.getDate(), 0, 0, 0, 0);

const initialState = {
  waypoints: [
    {
      id: 0,
      location: null,
      formatted_address: '',
      depart: null,
      arrive: null,
    },
  ],
  foodPackages: [],
  zoomedOnWaypoint: null,
  fullname: "",
  promo: "",
  email: "",
  phone: "",
  company: "",
  brand: "abuss",
  start: (new Date(Math.floor((DATE_NEXT_WEEK_00.getTime() - DATE_NEXT_WEEK_00.getTimezoneOffset() * 60000) / 60000.0) * 60000.0).toISOString()).slice(0, -1),
  passengers: 0,
  notes: "",
  queryId: null,
  sent: false,
};

const inquerySlice = createSlice({
  name: 'inquery',
  initialState,
  reducers: {

    finish: (state, { payload: { queryId } }) => ({ ...initialState, brand: state.brand, sent: true, queryId }),

    restart: (state, { payload }) => ({ ...initialState, brand: state.brand }),
    setBrand: (state, { payload: { brand } }) => {
      state.brand = brand;
    },
    updateRouteEstimation: (state, { payload: { route } }) => {
      // Check if the incoming route data is substantially different before updating
      // This basic check compares lengths and IDs, could be more sophisticated
      if (state.waypoints.length !== route.length) {
        console.log("Route length mismatch, updating state.");
        state.waypoints = route;
        return;
      }

      let changed = false;
      for (let i = 0; i < state.waypoints.length; i++) {
        // Compare relevant fields, especially 'arrive' which is expected to change
        if (
          state.waypoints[i].id !== route[i].id ||
          state.waypoints[i].arrive !== route[i].arrive || // Primarily check if arrive time changed
          state.waypoints[i].depart !== route[i].depart // Also check depart just in case
          // Add other fields if necessary, but keep it focused on what estimation affects
        ) {
          changed = true;
          break;
        }
      }

      if (changed) {
        console.log("Route estimation data changed, updating state.");
        // Update the state waypoints with the new data from the backend
        state.waypoints = route.map((newWp, index) => ({
            ...state.waypoints[index], // Keep existing state data like location, formatted_address
            ...newWp // Overwrite with new data (mainly arrive, potentially depart if backend modifies it)
        }));
      } else {
          console.log("Route estimation data unchanged, skipping state update.");
      }
    },
    setWaypoints: (state, { payload: { waypoints } }) => {
      state.waypoints = waypoints;
    },
    addWaypoint: (state, { payload: { waypoint } }) => {
      state.waypoints.push({
        ...waypoint,
        id: state.waypoints.reduce((max, waypoint) => Math.max(max, waypoint.id), -1) + 1,
        arrive: null,
      });
    },
    removeWaypoint: (state, { payload: { id } }) => {
      state.waypoints = state.waypoints.filter(waypoint => +waypoint.id !== +id);
      state.waypoints.forEach((waypoint) => {
        waypoint.arrive = null;
      });
    },
    updateWaypoint: (state, { payload: { id, location, formatted_address } }) => {
      const waypointToUpdate = state.waypoints.find((waypoint) => +waypoint.id === +id);

      if (waypointToUpdate) {
        if (location) {
            waypointToUpdate.location = location;
        }
        if (formatted_address !== undefined) {
             waypointToUpdate.formatted_address = formatted_address;
        }
        state.waypoints.forEach((waypoint) => {
            waypoint.arrive = null;
        });
      }
    },
    setWaypointDepart: (state, { payload: { id, depart } }) => {
      const waypointToUpdate = state.waypoints.find((waypoint) => +waypoint.id === +id);

      if (waypointToUpdate) {
        waypointToUpdate.depart = depart;
      }

      state.waypoints.forEach((waypoint) => {
        waypoint.arrive = null;
      });
    },
    swapWaypoints: (state, { payload: { fromIndex, toIndex } }) => {
      const newArray = Array.from(state.waypoints);
      const [ reorderedItem ] = newArray.splice(fromIndex, 1);
      newArray.splice(toIndex, 0, reorderedItem);

      state.waypoints = newArray;
      state.waypoints.forEach((waypoint) => {
        waypoint.arrive = null;
      });
    },

    addToCart: (state, { payload: { id } }) => {
      console.log("Adding to cart:", id);
      const index = state.foodPackages.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.foodPackages[index].count++;
      } else {
        state.foodPackages.push({ id, count: 1 });
      }
    },
    decrementFromCart: (state, { payload: { id } }) => {
      console.log("Decrementing from cart:", id);
      const index = state.foodPackages.findIndex((item) => item.id === id);
      if (index === -1) return;

      state.foodPackages[index].count--;
      if (state.foodPackages[index].count <= 0) {
        state.foodPackages.splice(index, 1);
      }
    },
    setCartCount: (state, { payload: { id, count } }) => {
      console.log("Setting cart count:", id, count);
      const index = state.foodPackages.findIndex((item) => item.id === id);
      if (index === -1) return;

      console.log("current:", state.foodPackages[index].count, "typeof", typeof state.foodPackages[index].count);

      if (count <= 0) {
        state.foodPackages.splice(index, 1);
        return;
      }

      state.foodPackages[index].count = +count;
    },

    zoomOnWaypoint: (state, { payload: { lat, lng } }) => {
      state.zoomedOnWaypoint = { lat, lng };
    },
    updateFullname: ( state, { payload: { fullname } } ) => {
      state.fullname = fullname;
    },
    updatePromo: ( state, { payload: { promo } } ) => {
      state.promo = promo;
    },
    updateEmail: ( state, { payload: { email } } ) => {
      state.email = email;
    },
    updatePhone: ( state, { payload: { phone } } ) => {
      state.phone = phone;
    },
    updateCompany: ( state, { payload: { company } } ) => {
      state.company = company;
    },
    updateStart: ( state, { payload: { start } } ) => {
      state.start = start;
    },
    updatePassengers: ( state, { payload: { passengers } } ) => {
      state.passengers = passengers;
    },
    updateNotes: ( state, { payload: { notes } } ) => {
      state.notes = notes;
    },
  },
});

export const { finish, restart, setWaypoints, addWaypoint, removeWaypoint, updateWaypoint, setWaypointDepart, swapWaypoints, zoomOnWaypoint, addToCart, decrementFromCart, setCartCount, updateFullname, updatePromo, updateEmail, updatePhone, updateStart, updatePassengers, updateNotes, updateCompany, setBrand } = inquerySlice.actions;
export default inquerySlice.reducer;
